import { useMemo, useState } from 'react'
import {
  DataTable,
  DateField,
  Label,
  MultiSelect,
  SearchInput,
  type DataTableSortState,
} from '@atrvd/saas-ui'
import { hasPermission } from '@dcp/shared'
import { useCreditCardAvailability, useFacilities, usePacks } from '@/hooks/useBanking'
import { useProfile } from '@/hooks/useProfile'
import { useTablePagination } from '@/hooks/useTablePagination'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'
import { queryErrorDisplay, tableQueryState } from '@/components/common/queryErrorDisplay'
import { SEARCH_DEBOUNCE_MS, useDebouncedValue } from '@/hooks/useDebouncedValue'
import {
  facilitiesForPackSelection,
  NO_PACK,
  UNASSIGNED_FACILITY,
  packFilterOptions,
  pruneFacilitySelection,
  toFilterEntries,
} from '@/components/ledgersync/connectionManagement.utils'
import type { CreditCardSortColumn } from '@/lib/db-tenant'
import { EditCreditCardDialog } from '@/components/banking/credit-cards/EditCreditCardDialog'
import { CreditCardTransactionsDialog } from '@/components/banking/credit-cards/CreditCardTransactionsDialog'
import { creditCardColumns } from './creditCardColumns'

/** AC#3: Facility ascending, then card name — the RPC's own default. */
const DEFAULT_SORT: DataTableSortState[] = [{ columnKey: 'facility', direction: 'asc' }]

/**
 * Credit Card Availability (DCP-374).
 *
 * Lists the organization's credit cards with the terms their banks publish —
 * BOTH the API-connected ones and the manual ones, per AC#1. Which cards
 * appear is the RPC's decision, not this component's: a card must not be
 * disconnected or provider-removed, must be assigned to a facility, and that
 * facility must be active.
 *
 * AVAILABILITY IS COMPUTED BY THE RPC, not taken from the feed. AC#8.2 defines
 * it as limit minus usage, which in our signs (what is owed is negative) is
 * `credit_limit + balance`. Empty whenever either operand is missing — a
 * partial sum would report a card as fully drawn on no evidence — while a
 * genuine zero stays zero and shows as $0.00, because "no credit left" is a
 * fact an AP user acts on. The vendor's own `available_credit` is returned
 * beside it and deliberately NOT rendered.
 *
 * Balances keep the vendor's sign, where what is OWED is negative.
 *
 * NOTHING IS FILTERED OR SORTED HERE either. The table is paged server-side,
 * so the component holds one page; the filters, the search term, the ordering
 * and the page all travel to the RPC.
 */
export function CreditCardAvailabilityPage() {
  const { data: facilities = [] } = useFacilities()
  const { data: packs = [] } = usePacks()

  /** Editing a card's figures needs `manage_banking`; the server enforces it too. */
  const { data: profile } = useProfile()
  const canManage = hasPermission(profile?.productRole, 'manage_banking')

  /** The card whose "Edit credit card" dialog is open; the row itself is read from the page. */
  const [editingId, setEditingId] = useState<string | null>(null)
  /** The card whose "View transactions" dialog is open, read from the page the same way. */
  const [transactionsId, setTransactionsId] = useState<string | null>(null)

  /**
   * `search` is what the input shows, updated on every keystroke; `searchTerm`
   * is what the query uses, settling {@link SEARCH_DEBOUNCE_MS} later. The term
   * is part of the RPC's query key, so an undebounced box would issue a request
   * per character and send the table back to page one mid-word. Trimmed before
   * the debounce, so a change that cannot alter the term (typing and deleting
   * a space) does not restart the timer.
   */
  const [search, setSearch] = useState('')
  const searchTerm = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS)
  const [packFilter, setPackFilter] = useState<string[]>([])
  const [facilityFilter, setFacilityFilter] = useState<string[]>([])
  const [dueFrom, setDueFrom] = useState('')
  const [dueTo, setDueTo] = useState('')
  const [sortState, setSortState] = useState<DataTableSortState[]>(DEFAULT_SORT)

  /** Only ACTIVE facilities can hold a visible card, so only they are offered. */
  const activeFacilities = useMemo(() => facilities.filter((f) => f.is_active), [facilities])

  /**
   * AC#7.1: once packs are picked, the Facility filter offers only their
   * facilities.
   *
   * NOT the shared `facilityFilterOptions`. Both narrow the facilities the same
   * way — a pack's own facilities, and the pack-less ones under "No Pack" — but
   * the shared helper also appends the UNASSIGNED_FACILITY sentinel (always
   * with no pack picked, and under "No Pack"), because the connection-accounts
   * table has accounts without a facility. A card without one cannot reach this
   * page at all (the RPC inner-joins facilities), so here that option could
   * only ever return nothing and is never offered.
   */
  const facilityOptions = useMemo(
    () =>
      facilitiesForPackSelection(activeFacilities, packFilter).map((f) => ({
        value: f.id,
        label: f.name,
      })),
    [activeFacilities, packFilter],
  )

  /**
   * Narrowing the options is not enough on its own: a facility chosen BEFORE a
   * pack was picked would stay in the query while vanishing from the dropdown —
   * an invisible filter whose symptom is an empty table with every control
   * saying "All".
   */
  const handlePackChange = (next: string[]) => {
    setPackFilter(next)
    setFacilityFilter((current) => {
      // Pruned against the options THIS page offers — the same function the
      // dropdown uses, so the two cannot disagree.
      const allowed = facilitiesForPackSelection(activeFacilities, next)
      return pruneFacilitySelection(current, allowed.map((f) => ({ value: f.id })))
    })
  }

  const sort = (sortState[0]?.columnKey ?? 'facility') as CreditCardSortColumn
  const direction: 'asc' | 'desc' = sortState[0]?.direction === 'desc' ? 'desc' : 'asc'

  const query = useMemo(
    () => ({
      search: searchTerm || undefined,
      packIds: toFilterEntries(packFilter, NO_PACK),
      facilityIds: toFilterEntries(facilityFilter, UNASSIGNED_FACILITY),
      dueFrom: dueFrom || undefined,
      dueTo: dueTo || undefined,
      sort,
      direction,
    }),
    [searchTerm, packFilter, facilityFilter, dueFrom, dueTo, sort, direction],
  )

  /**
   * An impossible window, typed rather than picked.
   *
   * `DateField`'s `min`/`max` only grey out days in the CALENDAR — saas-ui is
   * explicit that they are an affordance and that a typed out-of-range date
   * still reaches state. So "Due to before Due from" is reachable, and the RPC
   * answers it honestly with nothing, which reads on screen as "this
   * organization has no cards due" rather than "you asked for a window that
   * cannot contain a date". Detected here so the empty state can say which.
   */
  const dueRangeInverted = Boolean(dueFrom && dueTo && dueFrom > dueTo)

  const pagination = useTablePagination({
    // The total comes back with the page (`total_count`), see `forTotal` below.
    totalItems: undefined,
    // A different filter set or order makes page N an arbitrary slice.
    resetKey: JSON.stringify(query),
  })
  const { rows, totalCount, isPending, isFetching, isPlaceholderData, error, retry } =
    useCreditCardAvailability({
      ...query,
      page: pagination.page,
      pageSize: pagination.pageSize,
    })
  const tableState = tableQueryState(
    { isPending, isPlaceholderData, isFetching, error, refetch: retry },
    rows.length,
  )
  const errorDisplay = queryErrorDisplay(tableState, rows.length)
  const { dataTablePagination } = pagination.forTotal({
    total: totalCount,
    isFetching,
  })
  const editingRow = editingId === null ? undefined : rows.find((row) => row.id === editingId)
  const transactionsRow =
    transactionsId === null ? undefined : rows.find((row) => row.id === transactionsId)

  // A card that left the loaded page while its dialog was open (a filter, a
  // re-sort, a save that moved it) closes the dialog for good: kept, the id
  // would reopen it whenever the card came back into view. Set during render,
  // React's pattern for adjusting state to new data; never while a page is
  // still loading, when `rows` may be the previous page's placeholder.
  if (!isFetching && editingId !== null && !editingRow) setEditingId(null)
  if (!isFetching && transactionsId !== null && !transactionsRow) setTransactionsId(null)

  const columns = useMemo(
    () =>
      creditCardColumns({
        canManage,
        onEdit: setEditingId,
        onViewTransactions: setTransactionsId,
      }),
    [canManage],
  )

  const columnPrefs = useTableColumnPrefs('credit-card-availability', columns)

  return (
    // A COLUMN THAT EXACTLY FILLS ITS PARENT, so the page never produces a
    // scrollbar of its own. The layout's <main> is `flex-1 overflow-y-auto`:
    // anything taller than it scrolls THERE, and with a table that also
    // scrolls the user gets two bars side by side and no way to tell which one
    // moves the rows. Fixing the column to `h-full` and letting the table take
    // the leftover with `flex-1 min-h-0` leaves exactly one.
    <div className="flex h-full flex-col gap-4">
      <h1 className="text-2xl font-bold tracking-tight">Credit Card Availability</h1>

      {/*
        Search anchored LEFT, the pickers pushed RIGHT by `ml-auto`, with the
        space between them doing the giving and taking. Stretching search to
        fill that space instead made one text box dominate a row of narrow
        dropdowns.

        No transition is declared here: the sidebar animates its own width
        (`transition-[width] duration-300`), the content column follows it frame
        by frame, and a row laid out this way reflows with it — the controls
        glide apart on collapse rather than jumping.
      */}
      <div className="flex flex-nowrap items-end gap-3">
        <div className="w-[320px] shrink-0 space-y-1.5">
          <Label htmlFor="cc-search">Search</Label>
          <SearchInput
            id="cc-search"
            placeholder="Search by card name or number..."
            value={search}
            onChange={setSearch}
            clearable
          />
        </div>

        <div className="ml-auto flex flex-nowrap items-end gap-3">
          <div className="w-[190px] space-y-1.5">
            <Label>Pack</Label>
            <MultiSelect
              options={packFilterOptions(packs)}
              value={packFilter}
              onChange={handlePackChange}
              placeholder="All packs"
            />
          </div>
          <div className="w-[200px] space-y-1.5">
            <Label>Facility</Label>
            <MultiSelect
              options={facilityOptions}
              value={facilityFilter}
              onChange={setFacilityFilter}
              placeholder="All facilities"
            />
          </div>
          {/*
            Two independent fields rather than saas-ui's DateRangePicker: that
            component requires BOTH ends and works in `Date`, while AC#7.2
            allows either bound alone — and `DateField` is the repo's
            timezone-safe calendar input, built so a bare YYYY-MM-DD can never
            shift a day. The cross-bound min/max is AC#7.2's "end cannot precede
            start" — as a HINT, not a lock: it greys the impossible days out of
            each calendar, while a typed date still reaches state (saas-ui
            documents this), which is why `dueRangeInverted` above catches what
            slips past.
          */}
          <div className="w-[165px] space-y-1.5">
            <Label htmlFor="cc-due-from">Due from</Label>
            <DateField
              id="cc-due-from"
              value={dueFrom}
              onValueChange={setDueFrom}
              max={dueTo || undefined}
            />
          </div>
          <div className="w-[165px] space-y-1.5">
            <Label htmlFor="cc-due-to">Due to</Label>
            <DateField
              id="cc-due-to"
              value={dueTo}
              onValueChange={setDueTo}
              min={dueFrom || undefined}
            />
          </div>
        </div>
      </div>

      {errorDisplay.banner}
      <DataTable
        title="Credit Cards"
        // The prototype puts the count beside the heading. Kept even with a
        // pager, because the pager hides itself at 20 cards or fewer.
        subtitle={
          totalCount === undefined
            ? undefined
            : `${totalCount.toLocaleString('en-US')} ${totalCount === 1 ? 'card' : 'cards'}`
        }
        itemName="cards"
        columns={columns}
        enableColumnReorder
        {...columnPrefs}
        data={rows}
        rowKey={(row) => row.id}
        // `flex-1 min-h-0` rather than a pixel height: the table takes whatever
        // the column has left after the title and the filters, so it fits every
        // window without a magic number. `min-h-0` lets the flex child shrink
        // below its content, so the table's own area scrolls instead of the page.
        className="min-h-0 flex-1"
        stickyHeader
        sorting={{
          // SERVER mode: the table holds one page, so sorting it in memory
          // would order that page and not the set.
          mode: 'server',
          sortState,
          // The third click of a header clears the sort: back to the default order.
          onSortChange: (next) => setSortState(next.length > 0 ? next : DEFAULT_SORT),
        }}
        pagination={dataTablePagination}
        loading={tableState.loading}
        refreshing={tableState.refreshing}
        // The footer stays with a failed page, so the user can step back to one that loaded.
        error={errorDisplay.tableError}
        emptyState={
          <p className="text-sm text-muted-foreground">
            {dueRangeInverted
              ? 'Due to is earlier than Due from — no date can fall in that range.'
              : 'No credit cards match the filters.'}
          </p>
        }
      />

      {editingRow && (
        <EditCreditCardDialog
          key={editingRow.id}
          row={editingRow}
          onClose={() => setEditingId(null)}
        />
      )}

      {transactionsRow && (
        <CreditCardTransactionsDialog
          key={transactionsRow.id}
          card={transactionsRow}
          onClose={() => setTransactionsId(null)}
        />
      )}
    </div>
  )
}
